import { apiGet } from "@/utils/api";

// Module-level (not component-level) so starting polling twice from two
// different mounted components can't stack multiple intervals.
let _pollIntervalId = null;

const state = () => ({
  eligible: true,
  examsTakenToday: 0,
  dailyLimit: 2,
  breakMinutes: 15,
  nextEligibleAt: null,
  reason: null,
  loaded: false,
});

const getters = {
  eligible: (state) => state.eligible,
  examsTakenToday: (state) => state.examsTakenToday,
  dailyLimit: (state) => state.dailyLimit,
  breakMinutes: (state) => state.breakMinutes,
  nextEligibleAt: (state) => state.nextEligibleAt,
  reason: (state) => state.reason,
  eligibilityLoaded: (state) => state.loaded,
};

const mutations = {
  SET_ELIGIBILITY(state, metadata) {
    if (!metadata) return;
    state.eligible = metadata.eligible !== false;
    state.examsTakenToday = metadata.exams_taken_today ?? state.examsTakenToday;
    state.dailyLimit = metadata.daily_limit ?? state.dailyLimit;
    state.breakMinutes = metadata.break_minutes ?? state.breakMinutes;
    state.nextEligibleAt = metadata.next_eligible_at || null;
    state.reason = metadata.reason || null;
    state.loaded = true;
  },
};

const actions = {
  // Ingests metadata the caller already received from its own
  // /exams/student-exams-available/ call (e.g. the exam list fetch) --
  // no extra network request, just mirrors the server's answer.
  ingestEligibility({ commit }, metadata) {
    commit("SET_ELIGIBILITY", metadata);
  },

  // Asks only for eligibility (page_size: 1 keeps the payload tiny) --
  // used for periodic refresh and the pre-launch re-check, so neither one
  // touches the exam list / re-renders the rest of the page.
  async fetchEligibility({ commit }) {
    try {
      const res = await apiGet("/exams/student-exams-available/", {
        page_size: 1,
      });
      const metadata = res?.data?.metadata || null;
      if (metadata) {
        commit("SET_ELIGIBILITY", metadata);
      }
      return metadata;
    } catch (err) {
      console.error("[ExamEligibility] Failed to fetch eligibility:", err);
      return null;
    }
  },

  // Every refresh re-asks the server "am I eligible right now" and shows
  // whatever it says (reason, next_eligible_at as an absolute server
  // timestamp) -- there is no client-side countdown/deadline math here,
  // so changing the local clock has no effect on what gets displayed.
  startEligibilityPolling({ dispatch }, intervalMs = 45000) {
    if (_pollIntervalId) return;
    _pollIntervalId = setInterval(() => {
      dispatch("fetchEligibility");
    }, intervalMs);
  },

  stopEligibilityPolling() {
    if (_pollIntervalId) {
      clearInterval(_pollIntervalId);
      _pollIntervalId = null;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
